<div class="invoices-container">
    <!-- Header Section -->
    <div class="invoices-header">
        <div class="header-content">
            <div class="header-icon">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
            </div>
            <div class="header-text">
                <h1 class="page-title">Partner Invoices</h1>
                <p class="page-subtitle">View and manage your billing invoices</p>
            </div>
        </div>

        <div class="header-actions">
            <button class="btn-refresh" (click)="refreshInvoices()" [disabled]="isLoading">
                <i class="fas fa-sync-alt" [class.fa-spin]="isLoading"></i>
                Refresh
            </button>
        </div>
    </div>

    <!-- Loading State -->
    <div class="loading-section" *ngIf="isLoading">
        <div class="loading-spinner"></div>
        <p class="loading-text">Loading invoices...</p>
    </div>

    <!-- Error Message -->
    <div class="error-message" *ngIf="errorMessage && !isLoading">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span>{{ errorMessage }}</span>
    </div>

    <!-- Invoices Table -->
    <div class="invoices-content" *ngIf="!isLoading && !errorMessage">
        <!-- When invoices exist -->
        <div class="table-container" *ngIf="invoices.length > 0">
            <table class="invoices-table">
                <thead>
                    <tr>
                        <th>Invoice ID</th>
                        <th>Billing Period</th>
                        <th>Status</th>
                        <th class="text-right">Total Amount</th>
                        <th class="text-center">Transactions</th>
                        <th class="text-center">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    <ng-container *ngFor="let invoice of invoices">
                        <tr class="invoice-row">

                            <!-- Invoice ID -->
                            <td class="invoice-id-cell">
                                <div class="invoice-id-wrapper">
                                    <div class="invoice-icon">
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                                            stroke="currentColor">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                    </div>
                                    <span class="invoice-id">{{ invoice.invoiceId }}</span>
                                </div>
                            </td>

                            <!-- Billing Period -->
                            <td class="period-cell" data-label="Billing Period">
                                <span class="period-value">{{ formatDateRange(invoice.billingPeriodStart,
                                    invoice.billingPeriodEnd) }}</span>
                            </td>

                            <!-- Status -->
                            <td class="status-cell" data-label="Status">
                                <span class="status-badge" [ngClass]="getStatusClass(invoice.status)">
                                    {{ getStatusLabel(invoice.status) }}
                                </span>
                            </td>

                            <!-- Total Amount -->
                            <td class="amount-cell text-right" data-label="Total Amount">
                                <span class="amount-value">₹{{ invoice.totalAmount | number:'1.2-2' }}</span>
                            </td>

                            <!-- Transaction Count -->
                            <td class="count-cell text-center" data-label="Transactions">
                                <span class="count-badge">{{ invoice.transactionCount || 0 }}</span>
                            </td>

                            <!-- Actions -->
                            <td class="actions-cell text-center" data-label="Actions">
                                <button class="download-button" *ngIf="invoice.invoiceDownloadUrl"
                                    (click)="downloadInvoice(invoice, $event)" title="Download Invoice">
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                                        stroke="currentColor">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                            d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                </button>
                            </td>
                        </tr>
                    </ng-container>
                </tbody>
            </table>
        </div>

        <!-- Pagination Controls -->
        <div class="pagination-container" *ngIf="!isLoading && !errorMessage && invoices.length > 0">
            <div class="pagination-info">
                <p>Showing {{ (currentPage - 1) * pageSize + 1 }} to {{ Math.min(currentPage * pageSize, totalCount) }} of {{ totalCount }} invoices</p>
                <div class="page-size-selector">
                    <label for="pageSizeSelect">Items per page:</label>
                    <select id="pageSizeSelect" [(ngModel)]="pageSize" (ngModelChange)="onPageSizeChange($event)" class="page-size-dropdown">
                        <option [ngValue]="5">5</option>
                        <option [ngValue]="10">10</option>
                        <option [ngValue]="20">20</option>
                        <option [ngValue]="50">50</option>
                        <option [ngValue]="100">100</option>
                    </select>
                </div>
            </div>
            <div class="pagination-controls">
                <button class="btn-page" (click)="previousPage()" [disabled]="currentPage === 1">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                    </svg>
                    <span class="btn-text">Previous</span>
                </button>

                <div class="page-numbers">
                    <button *ngFor="let page of getPageNumbers()" class="btn-page-number"
                        [class.active]="page === currentPage" (click)="goToPage(page)">
                        {{ page }}
                    </button>
                </div>

                <button class="btn-page" (click)="nextPage()" [disabled]="currentPage === totalPages">
                    <span class="btn-text">Next</span>
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                    </svg>
                </button>
            </div>
        </div>

        <!-- Empty State - No Invoices -->
        <div class="empty-state" *ngIf="invoices.length === 0">
            <div class="empty-state-icon">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
            </div>
            <h3 class="empty-state-title">No Invoices Found</h3>
            <p class="empty-state-text">You don't have any invoices yet</p>
        </div>
    </div>
</div>